/**
 * Single toggle pill — press to activate, press again to deactivate.
 * Compose multiple pills side-by-side for a multi-select group.
 */

import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Props for {@link TogglePill}. */
interface TogglePillProps {
  label: string;
  active: boolean;
  onClick: () => void;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
  icon?: LucideIcon;
}

/** Padding and type size for each toggle-pill size. */
const sizeClasses: Record<NonNullable<TogglePillProps['size']>, string> = {
  sm: 'px-2.5 py-1 text-xs rounded-md',
  md: 'px-3.5 py-1.5 text-sm rounded-lg',
  lg: 'px-4 py-2 text-sm rounded-lg',
};

/** Pill-shaped on/off toggle used in compact filter rows. */
export const TogglePill: React.FC<TogglePillProps> = ({
  label,
  active,
  onClick,
  size = 'md',
  disabled = false,
  className,
  icon: Icon,
}) => {
  const handleClick = () => {
    if (!disabled) onClick();
  };

  function renderIcon(): React.ReactNode {
    if (!Icon) return null;
    return <Icon className="w-3 h-3 shrink-0" />;
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        sizeClasses[size],
        'inline-flex items-center justify-center gap-1 font-medium border-[1.5px] transition-all duration-200',
        active
          ? 'bg-[var(--color-2-nuance-1)] text-white border-[var(--color-2-nuance-1)]'
          : 'bg-transparent text-foreground border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] hover:border-[var(--color-1-nuance-2)]',
        disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
    >
      {renderIcon()}
      {label}
    </button>
  );
};
